<template>
  <div class="pdf-viewer-container" :class="{ 'pdf-viewer-light': lightTheme }">
    <!-- Contenido principal del PDF -->
    <div class="pdf-main-content">
    <!-- Toolbar de navegación -->
    <div class="pdf-toolbar" v-if="totalPages > 0 && !hideToolbar">
      <div class="pdf-toolbar-content">
        
        <!-- Grupo izquierdo -->
        <div class="toolbar-group toolbar-left">
          <button 
            class="control-btn action-btn"
            @click="toggleThumbnails"
            title="Menú"
          >
            <i class="fa fa-th"></i>
          </button>       
      </div>

        <!-- Grupo central -->
        <div class="toolbar-group toolbar-center">
          <div class="page-badge">
            <div class="page-navigation">
              <span class="page-label">Página</span>
              <input 
                type="number" 
                class="page-input"
                v-model.number="currentPage"
                @keyup.enter="goToPage"
                @blur="goToPage"
                min="1"
                :max="totalPages"
              />
              <span class="page-total">de {{ totalPages }}</span>
            </div>
          </div>

          <div class="divider"></div>       

          <div class="zoom-controls">
            <button 
              class="control-btn zoom-btn"
          @click="zoomOut"
          :disabled="scale <= 0.5"
              title="Alejar (Ctrl + Scroll abajo)"
        >
          <i class="fa fa-search-minus"></i>
            </button>
          
            <div class="zoom-input-container">
              <input 
                type="text" 
                v-model.number="zoomInput" 
                @keyup.enter="applyZoomFromInput"
                @blur="applyZoomFromInput"
                class="zoom-input"
                min="10"
                max="400"
                step="5"
              />
              <span class="zoom-percent">%</span>
            </div>
          
            <button 
              class="control-btn zoom-btn"
          @click="zoomIn"
              :disabled="scale >= 4"
              title="Acercar (Ctrl + Scroll arriba)"
        >
          <i class="fa fa-search-plus"></i>
            </button>
          </div>

          <div class="divider"></div>       

          <button 
            class="control-btn action-btn"
          @click="resetZoom"
            title="Ajustar a la página"
          >
            <i class="fa fa-arrows-alt"></i>
          </button>
        </div>

        <!-- Grupo derecho -->
        <div class="toolbar-group toolbar-right">
          <button 
            class="control-btn action-btn"
            @click="downloadPdf"
            title="Descargar PDF"
          >
            <i class="fa fa-download"></i>
          </button>

          <button 
            class="control-btn action-btn"
            @click="printPdf"
            title="Imprimir PDF"
          >
            <i class="fa fa-print"></i>
          </button>
        </div>
      </div>
    </div>

      <!-- Contenedor del contenido PDF con miniaturas -->
      <div class="pdf-content-container">
        <!-- Panel lateral de miniaturas -->
        <div class="thumbnail-panel" :class="{ show: showThumbnails && totalPages > 0 }" v-show="showThumbnails && totalPages > 0">
         
          <div class="thumbnail-list" ref="thumbnailContainer">
            <div 
              v-for="pageNum in totalPages" 
              :key="`thumb-${pageNum}`"
              class="thumbnail-item"
              :class="{ active: pageNum === currentPage }"
              @click="goToPageFromThumbnail(pageNum)"
            >
              <canvas 
                :ref="`thumbnail-${pageNum}`"
                class="thumbnail-canvas"
              ></canvas>
              <div class="thumbnail-number">{{ pageNum }}</div>
            </div>
            
           
      </div>
    </div>

    <!-- Área de renderizado del PDF -->
        <div class="pdf-content" ref="pdfContent" @wheel="handleWheel" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
      <div class="pdf-content-inner">
        <!-- Canvas para cada página del PDF -->
        <div v-for="pageNum in totalPages" :key="pageNum" class="pdf-page-container">
          <canvas :ref="`pdfCanvas-${pageNum}`" v-show="!loading && !error"></canvas>
        </div>
    </div>

    <!-- Loading -->
      <div v-if="loading" class="pdf-loading-inline">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Cargando PDF...</p>
    </div>

    <!-- Error -->
        <div v-if="error" class="pdf-error-inline">
      <i class="fa fa-exclamation-triangle text-danger" style="font-size: 48px;"></i>
      <p class="mt-2 text-danger">{{ error }}</p>
          <button 
            class="btn btn-primary mt-3"
            @click="retryLoadPdf"
            title="Reintentar carga del PDF"
          >
            <i class="fa fa-refresh"></i>
            Reintentar
          </button>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

// Configurar el worker loca
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: 'PdfViewer',

  props: {
    src: {
      type: String,
      required: false,
      default: null
    },
    base64Content: {
      type: String,
      required: false,
      default: null
    },
    mimeType: {
      type: String,
      required: false,
      default: 'application/pdf'
    },
    initialScale: {
      type: Number,
      default: 1.0
    },
    fileName: {
      type: String,
      default: null
    },
    hideToolbar: {
      type: Boolean,
      default: false
    },
    autoFit: {
      type: Boolean,
      default: false
    },
    lightTheme: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: this.initialScale,
      zoomInput: Math.round(this.initialScale * 100),
      loading: false,
      error: null,
      isMouseOverPdf: false,
      lastZoomTime: 0,
      showThumbnails: false,
      thumbnailRenderTasks: new Map(),
      pageRenderTasks: new Map(),
      interceptionSetup: false,
      thumbnailsReady: false,
      downloadClickListener: null,
      blobUrl: null, // Para almacenar el blob URL creado desde base64
      
      // Constantes de configuración
      ZOOM_FACTOR: 0.2,
      ZOOM_WHEEL_FACTOR: 0.15,
      MIN_ZOOM: 0.5,
      MAX_ZOOM: 4.0,
      THUMBNAIL_SCALE: 0.5,
      FIXED_ZOOM_SCALE: 0.90,
      ZOOM_DEBOUNCE_TIME: 100,
      RENDER_DELAY: 100,
      
      // Constantes de layout y scroll
      CONTAINER_PADDING: 40,
      HORIZONTAL_PADDING: 20,
      VERTICAL_MARGIN: 20,
      THUMBNAIL_PANEL_WIDTH: 220,
      THUMBNAIL_HEIGHT: 100,
      THUMBNAIL_MIN_HEIGHT: 120
    };
  },

  watch: {
    src: {
      immediate: false,
      async handler(newSrc, oldSrc) {
        // Si hay un PDF anterior, limpiarlo primero
        if (oldSrc) {
          this.cleanup();
        }
        
        if (newSrc) {
          await this.$nextTick();
          this.loadPdf();
        }
      }
    },
    base64Content: {
      immediate: false,
      async handler(newContent, oldContent) {
        // Si hay un PDF anterior, limpiarlo primero
        if (oldContent) {
          this.cleanup();
        }
        
        if (newContent) {
          await this.$nextTick();
          this.loadPdf();
        }
      }
    }
  },

  mounted() {
    // Esperar a que el canvas esté disponible antes de cargar el PDF
    if (this.src || this.base64Content) {
      this.$nextTick(() => {
        // Esperar un frame adicional para asegurar que el canvas está en el DOM
        requestAnimationFrame(() => {
          this.loadPdf();
        });
      });
    }

    // Detectar la página actual al hacer scroll
    this.$nextTick(() => {
      const container = this.$refs.pdfContent;
      if (container) {
        container.addEventListener('scroll', this.updateCurrentPage);
      }
    });

    // Interceptar eventos de descarga del navegador para PDF.js
    this.interceptPdfJsControls();

  },

  beforeDestroy() {
    // Remover listener de descargas del documento
    if (this.downloadClickListener) {
      document.removeEventListener('click', this.downloadClickListener, true);
      this.downloadClickListener = null;
    }
    
    // Remover listener de scroll
    const container = this.$refs.pdfContent;
    if (container) {
      container.removeEventListener('scroll', this.updateCurrentPage);
    }
    this.cleanup();
  },

  methods: {
    handleError(error, context = '') {
      console.error(`Error ${context}:`, error);
      
      if (error.name === 'RenderingCancelledException') {
        return;
      }
      
      if (error.message.includes('Invalid PDF') || error.message.includes('no es un PDF válido')) {
        this.error = 'El archivo PDF no es válido o está corrupto';
      } else if (error.message.includes('fetch') || error.message.includes('network')) {
        this.error = 'No se pudo acceder al archivo PDF';
      } else if (error.message.includes('password')) {
        this.error = 'El PDF está protegido con contraseña';
      } else {
        this.error = `Error al cargar el PDF: ${error.message}`;
      }
    },

    async loadPdf() {
      this.loading = true;
      this.error = null;

      try {
        let pdfSource = null;
        
        // Si hay base64Content, convertirlo a ArrayBuffer
        if (this.base64Content) {
          const byteCharacters = atob(this.base64Content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          
          // Validar que sea un PDF
          const header = String.fromCharCode(...byteArray.slice(0, 4));
          if (!header.startsWith('%PDF')) {
            throw new Error('El archivo no es un PDF válido');
          }
          
          pdfSource = byteArray;
          
          // Crear blob URL para descargas
          const blob = new Blob([byteArray], { type: this.mimeType });
          this.blobUrl = window.URL.createObjectURL(blob);
        }
        // Si hay src, usarlo como antes
        else if (this.src) {
          pdfSource = this.src;
          
          if (this.src.startsWith('blob:')) {
            const response = await fetch(this.src);
            const arrayBuffer = await response.arrayBuffer();
            
            const firstBytes = new Uint8Array(arrayBuffer.slice(0, 4));
            const header = String.fromCharCode(...firstBytes);
            
            if (!header.startsWith('%PDF')) {
              throw new Error('El archivo no es un PDF válido');
            }
            
            pdfSource = arrayBuffer;
          }
        }
        else {
          throw new Error('No se proporcionó src ni base64Content');
        }

        const loadingTask = pdfjsLib.getDocument(pdfSource);
        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        this.currentPage = 1;
        this.thumbnailsReady = false; 

        if (this.autoFit) {
          await this.calculateFitScale();
        }

        await this.renderAllPages();
        
        this.loading = false;
      } catch (err) {
        this.handleError(err, 'al cargar PDF');
        this.loading = false;
      }
    },

    async renderPagesOnly() {
      if (!this.pdfDoc) return;
      
      this.cancelPageRenders();
      
      const renderPromises = [];
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        renderPromises.push(this.renderPage(pageNum));
      }
      
      await Promise.all(renderPromises);
      await this.$nextTick();
      
      setTimeout(() => {
        this.forceScrollUpdate();
        this.centerCanvas();
        this.ensureScrollWorks();
      }, 100);
    },
    centerCanvas() {
      const content = this.$refs.pdfContent;
      if (!content) return;
      
      const innerContent = content.querySelector('.pdf-content-inner');
      if (!innerContent) return;
      
      const canvas = content.querySelector('canvas');
      if (!canvas) return;
      
      const containerWidth = content.clientWidth;
      const containerHeight = content.clientHeight;
      const canvasWidth = canvas.offsetWidth;
      const canvasHeight = canvas.offsetHeight;
      
      const scrollInfo = this.getScrollInfo(content);
      
      if (canvasWidth < containerWidth && canvasHeight < containerHeight) {
        content.scrollLeft = 0;
        content.scrollTop = 0;
        } else {
        const { maxScrollLeft, maxScrollTop } = scrollInfo;
        
        if (maxScrollLeft > 0 || maxScrollTop > 0) {
          content.scrollLeft = Math.max(0, maxScrollLeft / 2);
          content.scrollTop = Math.max(0, maxScrollTop / 2);
        }
        
        setTimeout(() => {
          const finalScrollLeft = Math.min(content.scrollLeft, maxScrollLeft);
          const finalScrollTop = Math.min(content.scrollTop, maxScrollTop);
          
          content.scrollLeft = finalScrollLeft;
          content.scrollTop = finalScrollTop;
        }, 50);
      }
    },

    getScrollInfo(content) {
      return {
        scrollWidth: content.scrollWidth,
        scrollHeight: content.scrollHeight,
        clientWidth: content.clientWidth,
        clientHeight: content.clientHeight,
        maxScrollLeft: content.scrollWidth - content.clientWidth,
        maxScrollTop: content.scrollHeight - content.clientHeight,
        canScrollHorizontally: content.scrollWidth > content.clientWidth,
        canScrollVertically: content.scrollHeight > content.clientHeight
      };
    },

    forceScrollUpdate() {
      const content = this.$refs.pdfContent;
      if (!content) return;
      
      content.style.overflow = 'hidden';
      content.offsetHeight;
      content.style.overflow = 'auto';
      
      this.adjustInnerContainerSize();
    },

    ensureScrollWorks() {
      const content = this.$refs.pdfContent;
      if (!content) return;
      
      const originalScrollLeft = content.scrollLeft;
      const originalScrollTop = content.scrollTop;
      
      content.scrollTop = 0;
      content.scrollLeft = 0;
      
      setTimeout(() => {
        content.scrollLeft = originalScrollLeft;
        content.scrollTop = originalScrollTop;
      }, 10);
    },

    adjustInnerContainerSize() {
      const content = this.$refs.pdfContent;
      if (!content) return;
      
      const innerContent = content.querySelector('.pdf-content-inner');
      if (!innerContent) return;
      
      const canvas = content.querySelector('canvas');
      if (!canvas) return;
      
      const canvasWidth = canvas.offsetWidth;
      const canvasHeight = canvas.offsetHeight;
      const horizontalPadding = this.autoFit ? 0 : this.CONTAINER_PADDING; 
      const verticalMargin = this.autoFit ? 0 : this.VERTICAL_MARGIN;
      
      const containerWidth = content.clientWidth;
      const containerHeight = content.clientHeight;
      
      if (canvasWidth < containerWidth && canvasHeight < containerHeight) {
        innerContent.style.minWidth = '';
        innerContent.style.minHeight = '';
      } else {
        const minWidth = Math.max(canvasWidth + horizontalPadding, containerWidth);
        const minHeight = Math.max(canvasHeight + verticalMargin, containerHeight);
        
        innerContent.style.minWidth = `${minWidth}px`;
        innerContent.style.minHeight = `${minHeight}px`;
      }
    },

    centerPdfOnLoad() {
      const content = this.$refs.pdfContent;
      if (!content) return;
      
      const canvas = content.querySelector('canvas');
      if (!canvas) return;
      
      this.adjustInnerContainerSize();
      
      requestAnimationFrame(() => {
        const containerWidth = content.clientWidth;
        const containerHeight = content.clientHeight;
        const canvasWidth = canvas.offsetWidth;
        const canvasHeight = canvas.offsetHeight;
        
        if (canvasWidth < containerWidth && canvasHeight < containerHeight) {
          content.scrollLeft = 0;
          content.scrollTop = 0;
        }
      });
    },

    async renderAllPages() {
      if (!this.pdfDoc) return;

      const renderPromises = [];
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        renderPromises.push(this.renderPage(pageNum));
      }

      await Promise.all(renderPromises);
      
      await this.$nextTick();
      setTimeout(() => {
        this.centerPdfOnLoad();
      }, 100);

      setTimeout(async () => {
        await this.renderThumbnailsInBackground();
      }, 200);
    },

    getPageCanvas(pageNumber) {
      const canvasRef = this.$refs[`pdfCanvas-${pageNumber}`];
      return Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
    },

    validateAndConfigureViewport(page, pageNumber, scale) {
      const viewport = page.getViewport(scale);
      
      if (this.isValidViewport(viewport)) {
        return viewport;
      }
      
      console.warn(`Viewport inválido para página ${pageNumber}, usando escala por defecto`);
      const defaultViewport = page.getViewport(1.0);
      
      if (!this.isValidViewport(defaultViewport)) {
        throw new Error(`No se pudieron obtener dimensiones del PDF para página ${pageNumber}`);
      }
      
      return defaultViewport;
    },

    isValidViewport(viewport) {
      return !isNaN(viewport.width) && !isNaN(viewport.height) && 
             viewport.width > 0 && viewport.height > 0;
    },

    configureCanvas(canvas, viewport) {
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext('2d');
      context.clearRect(0, 0, canvas.width, canvas.height);
      return context;
    },

    applyFixedZoomIfNeeded(page) {
      if (this.currentPage === 1 && this.scale === this.initialScale) {
        const scaleDiff = Math.abs(this.FIXED_ZOOM_SCALE - this.initialScale);
        
        if (scaleDiff > 0.05) {
          this.scale = this.FIXED_ZOOM_SCALE;
          this.zoomInput = Math.round(this.FIXED_ZOOM_SCALE * 100);
          return page.getViewport(this.scale);
        }
      }
      return null;
    },
    
    async calculateFitScale() {
      const container = this.$refs.pdfContent;
      if (!container || !this.pdfDoc) return;
      
      // Asegurar que el contenedor tenga dimensiones
      if (container.clientWidth === 0 || container.clientHeight === 0) {
        // Si no tiene dimensiones, esperar un poco o usar valores por defecto
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      const padding = this.lightTheme ? 40 : (this.autoFit ? 0 : this.CONTAINER_PADDING);
      const containerWidth = container.clientWidth - padding;
      
      if (containerWidth <= 0) return;

      try {
        const page = await this.pdfDoc.getPage(1);
        const baseViewport = page.getViewport(1.0);
        
        // Fit Width: ajustar al ancho del contenedor para maximizar tamaño visible
        let newScale = containerWidth / baseViewport.width;
        
        // Respetar límites globales de zoom
        newScale = Math.max(this.MIN_ZOOM, Math.min(newScale, this.MAX_ZOOM));
        
        this.scale = newScale;
        this.zoomInput = Math.round(newScale * 100);
      } catch (error) {
        console.warn('Error calculando auto-fit:', error);
      }
    },

    async renderPage(pageNumber) {
      if (!this.pdfDoc) return;

      try {
        const page = await this.pdfDoc.getPage(pageNumber);
        const canvas = this.getPageCanvas(pageNumber);
        
        if (!canvas) {
          console.error(`Canvas no encontrado para página ${pageNumber}`);
          return;
        }

        let viewport = this.validateAndConfigureViewport(page, pageNumber, this.scale);
        
        const fixedViewport = this.applyFixedZoomIfNeeded(page);
        if (fixedViewport) {
          viewport = fixedViewport;
        }

        const context = this.configureCanvas(canvas, viewport);

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport
        });
        
        this.pageRenderTasks.set(pageNumber, renderTask);
        await renderTask.promise;
        this.pageRenderTasks.delete(pageNumber);

      } catch (err) {
        this.pageRenderTasks.delete(pageNumber);
        this.handleError(err, `al renderizar página ${pageNumber}`);
      }
    },

    async applyZoom(newScale) {
      if (newScale >= this.MIN_ZOOM && newScale <= this.MAX_ZOOM) {
        this.scale = newScale;
        this.zoomInput = Math.round(newScale * 100);
        await this.renderPagesOnly();
        return true;
      }
      return false;
    },

    async zoomIn() {
      const newScale = Math.min(this.scale + this.ZOOM_FACTOR, this.MAX_ZOOM);
      await this.applyZoom(newScale);
    },

    async zoomOut() {
      const newScale = Math.max(this.scale - this.ZOOM_FACTOR, this.MIN_ZOOM);
      await this.applyZoom(newScale);
    },

    async applyZoomFromInput() {
      const newScale = this.zoomInput / 100;
      
      if (!await this.applyZoom(newScale)) {
        this.zoomInput = Math.round(this.scale * 100);
        console.warn(`Zoom fuera de rango. Rango permitido: ${this.MIN_ZOOM * 100}% - ${this.MAX_ZOOM * 100}%`);
      }
    },

    async resetZoom() {
      const padding = this.autoFit ? 0 : this.CONTAINER_PADDING;
      const containerWidth = this.$refs.pdfContent.clientWidth - padding;
      const containerHeight = this.$refs.pdfContent.clientHeight - padding;
      
      const page = await this.pdfDoc.getPage(this.currentPage);
      const baseViewport = page.getViewport(1.0);
      
      const scaleX = containerWidth / baseViewport.width;
      const scaleY = containerHeight / baseViewport.height;
      const autoScale = Math.min(scaleX, scaleY, 1);
      
      await this.applyZoom(autoScale);
    },

    async handleWheel(event) {
      if (!event.ctrlKey) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      const now = Date.now();
      if (now - this.lastZoomTime < this.ZOOM_DEBOUNCE_TIME) return;
      this.lastZoomTime = now;
      
      const delta = event.deltaY;
      const oldScale = this.scale;
      let newScale;
      
      if (delta < 0) {
        newScale = Math.min(this.scale + this.ZOOM_WHEEL_FACTOR, this.MAX_ZOOM);
      } else {
        newScale = Math.max(this.scale - this.ZOOM_WHEEL_FACTOR, this.MIN_ZOOM);
      }
      
      if (newScale !== oldScale) {
        await this.applyZoom(newScale);
        
        this.$nextTick(() => {
          this.centerCanvas();
        });
      }
    },

    onMouseEnter() {
      this.isMouseOverPdf = true;
    },

    onMouseLeave() {
      this.isMouseOverPdf = false;
    },

    updateCurrentPage() {
      const container = this.$refs.pdfContent;
      if (!container) return;

      const containerTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const containerMiddle = containerTop + containerHeight / 2;

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const pageContainer = container.querySelector(`.pdf-page-container:nth-child(${pageNum})`);
        if (pageContainer) {
          const pageTop = pageContainer.offsetTop;
          const pageBottom = pageTop + pageContainer.offsetHeight;
          
          if (containerMiddle >= pageTop && containerMiddle <= pageBottom) {
            this.currentPage = pageNum;
            break;
          }
        }
      }
    },

    goToPage() {
      if (!this.currentPage || this.currentPage < 1 || this.currentPage > this.totalPages) {
        this.currentPage = Math.max(1, Math.min(this.currentPage || 1, this.totalPages));
          return;
        }

      const container = this.$refs.pdfContent;
      if (!container) return;

      const pageContainer = container.querySelector(`.pdf-page-container:nth-child(${this.currentPage})`);
      if (pageContainer) {
        pageContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    async toggleThumbnails() {
      if (this.showThumbnails) {
        this.showThumbnails = false;
      } else {
        this.showThumbnails = true;
        await this.$nextTick();
        
        if (!this.thumbnailsReady) {
          setTimeout(async () => {
            await this.renderThumbnailsSimple();
          }, 50);
        }
      }
    },

    goToPageFromThumbnail(pageNum) {
      this.currentPage = pageNum;
      this.goToPage();
    },


    createTempCanvas(viewport) {
      const tempCanvas = document.createElement('canvas');
      const tempContext = tempCanvas.getContext('2d');
      
      tempCanvas.width = viewport.width;
      tempCanvas.height = viewport.height;
      
      return { canvas: tempCanvas, context: tempContext };
    },

    async renderTempThumbnail(page) {
      const viewport = page.getViewport(this.THUMBNAIL_SCALE);
      const { canvas: tempCanvas, context: tempContext } = this.createTempCanvas(viewport);
      
      const renderContext = {
        canvasContext: tempContext,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      
      return {
        canvas: tempCanvas,
        width: tempCanvas.width,
        height: tempCanvas.height
      };
    },

    transferThumbnailToReal(pageNum, thumbnailData) {
      const thumbnailRef = this.$refs[`thumbnail-${pageNum}`];
      
      if (thumbnailRef && thumbnailRef[0] && thumbnailData.has(pageNum)) {
        const canvas = thumbnailRef[0];
        const data = thumbnailData.get(pageNum);
        
        canvas.width = data.width;
        canvas.height = data.height;
        
        const context = canvas.getContext('2d');
        context.drawImage(data.canvas, 0, 0);
      }
    },

    async renderThumbnailsInBackground() {
      if (!this.pdfDoc) return;

      const thumbnailData = new Map();

      // Renderizar miniaturas en segundo plano sin mostrarlas
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        try {
          const page = await this.pdfDoc.getPage(pageNum);
          const data = await this.renderTempThumbnail(page);
          thumbnailData.set(pageNum, data);
        } catch (error) {
          this.handleError(error, `renderizando miniatura en segundo plano ${pageNum}`);
        }
      }

      // Transferir a los canvas reales pero mantenerlos ocultos
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        this.transferThumbnailToReal(pageNum, thumbnailData);
      }

      // Marcar que las miniaturas están listas
      this.thumbnailsReady = true;
    },

    async renderThumbnailsSimple() {
      if (!this.pdfDoc) return;

      // Si las miniaturas ya están renderizadas en segundo plano, solo mostrarlas
      if (this.thumbnailsReady) {
        return;
      }

      const thumbnailData = new Map();

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        try {
          const page = await this.pdfDoc.getPage(pageNum);
          const data = await this.renderTempThumbnail(page);
          thumbnailData.set(pageNum, data);
        } catch (error) {
          this.handleError(error, `renderizando página ${pageNum}`);
        }
      }

      setTimeout(() => {
        for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
          this.transferThumbnailToReal(pageNum, thumbnailData);
        }
      }, 50);
    },

    cancelThumbnailRenders() {
      this.thumbnailRenderTasks.forEach((renderTask) => {
        try {
          renderTask.cancel();
        } catch (error) {
          // Silenciar errores de cancelación
        }
      });
      this.thumbnailRenderTasks.clear();
    },

    cancelPageRenders() {
      this.pageRenderTasks.forEach((renderTask) => {
        try {
          renderTask.cancel();
        } catch (error) {
          // Silenciar errores de cancelación
        }
      });
      this.pageRenderTasks.clear();
    },

    async renderThumbnails() {
      if (!this.pdfDoc || !this.showThumbnails) return;

      this.cancelThumbnailRenders();
      await this.$nextTick();

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        try {
          const page = await this.pdfDoc.getPage(pageNum);
          const thumbnailCanvas = this.$refs[`thumbnail-${pageNum}`];
          
          if (thumbnailCanvas && thumbnailCanvas[0]) {
            const canvas = thumbnailCanvas[0];
            const thumbnailScale = 0.5;
            const viewport = page.getViewport(thumbnailScale);
            
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            const context = canvas.getContext('2d');
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            
            const renderTask = page.render({
              canvasContext: context,
              viewport: viewport
            });
            
            this.thumbnailRenderTasks.set(pageNum, renderTask);
            await renderTask.promise;
            this.thumbnailRenderTasks.delete(pageNum);
          }
        } catch (error) {
          if (error.name !== 'RenderingCancelledException') {
            console.error(`Error al renderizar miniatura ${pageNum}:`, error);
          }
          this.thumbnailRenderTasks.delete(pageNum);
        }
      }
    },

    // Descargar el PDF
    downloadPdf() {
      const downloadUrl = this.blobUrl || this.src;
      
      if (!downloadUrl) return;
      
      // Crear un enlace temporal para descargar
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Usar nombre personalizado si está disponible, sino usar el nombre por defecto
      link.download = this.fileName || `documento_${Date.now()}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // Reintentar la carga del PDF
    async retryLoadPdf() {
      this.error = null;
      this.loading = true;
      
      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }
      
      await this.loadPdf();
    },


    // Imprimir el PDF
    printPdf() {
      const printUrl = this.blobUrl || this.src;
      
      if (!printUrl) return;
      
      // Crear un iframe oculto para imprimir sin abrir nueva ventana
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      printFrame.style.visibility = 'hidden';
      
      document.body.appendChild(printFrame);
      
      printFrame.onload = () => {
        // Esperar a que el PDF se cargue completamente en el iframe
        setTimeout(() => {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            
            // Escuchar cuando el usuario termina de imprimir
            const checkPrintDialog = () => {
              // Esperar 5 segundos antes de limpiar (tiempo suficiente para imprimir)
              setTimeout(() => {
                if (printFrame.parentNode) {
                  document.body.removeChild(printFrame);
                }
              }, 5000);
            };
            
            // Detectar cuando se cierra el diálogo de impresión
            if (printFrame.contentWindow.matchMedia) {
              const mediaQueryList = printFrame.contentWindow.matchMedia('print');
              mediaQueryList.addListener(() => {
                checkPrintDialog();
              });
            } else {
              // Si no soporta matchMedia, usar timeout largo
              checkPrintDialog();
            }
            
          } catch (e) {
            console.error('Error al imprimir:', e);
            // Limpiar en caso de error
            if (printFrame.parentNode) {
              document.body.removeChild(printFrame);
            }
          }
        }, 500);
      };
      
      // Manejar errores de carga
      printFrame.onerror = () => {
        console.error('Error al cargar PDF para imprimir');
        if (printFrame.parentNode) {
          document.body.removeChild(printFrame);
        }
      };
      
      printFrame.src = printUrl;
    },

    // Interceptar controles nativos de PDF.js
    interceptPdfJsControls() {
      // Solo interceptar si hay nombre personalizado
      if (!this.fileName) return;

      // Si ya hay un listener, removerlo primero
      if (this.downloadClickListener) {
        document.removeEventListener('click', this.downloadClickListener, true);
        this.downloadClickListener = null;
      }

      // Crear y guardar la referencia al listener
      this.downloadClickListener = (event) => {
        const target = event.target;
        
        // Solo interceptar si es nuestro botón de descarga específico
        if (target.closest('.control-btn') && 
            target.closest('.control-btn').querySelector('.fa-download') &&
            this.fileName) {
          
          event.preventDefault();
          event.stopPropagation();
          
          // Usar nuestro método de descarga
          this.downloadPdf();
          
          return false;
        }
      };

      // Agregar el listener al documento
      document.addEventListener('click', this.downloadClickListener, true);
      this.interceptionSetup = true;
    },


    cleanup() {
      // Remover listener de descargas si existe
      if (this.downloadClickListener) {
        document.removeEventListener('click', this.downloadClickListener, true);
        this.downloadClickListener = null;
        this.interceptionSetup = false;
      }
      
      // Revocar blob URL si existe
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
      
      // Cancelar todas las operaciones de renderizado
      this.cancelThumbnailRenders();
      this.cancelPageRenders();
      
      // Destruir el documento PDF anterior
      if (this.pdfDoc) {
        try {
          this.pdfDoc.destroy();
        } catch (error) {
          console.warn('Error al destruir PDF:', error);
        }
        this.pdfDoc = null;
      }
      
      // Resetear el estado
      this.currentPage = 1;
      this.totalPages = 0;
      this.thumbnailsReady = false;
      this.error = null;
      this.loading = false;
    }
  }
};
</script>

<style scoped>
/* Variables CSS para consistencia */
:root {
  --pdf-bg-primary: #525252;
  --pdf-bg-secondary: #f0f3f5;
  --pdf-bg-white: #ffffff;
  --pdf-border-color: #e1e4e8;
  --pdf-text-primary: #24292e;
  --pdf-text-secondary: #586069;
  --pdf-shadow-light: 0 1px 3px rgba(0, 0, 0, 0.05);
  --pdf-shadow-medium: 0 2px 4px rgba(0, 0, 0, 0.1);
  --pdf-shadow-heavy: 0 2px 8px rgba(0, 0, 0, 0.3);
  --pdf-border-radius: 4px;
  --pdf-border-radius-large: 16px;
  --pdf-transition: all 0.2s ease;
}

.pdf-viewer-container {
  display: flex;
  flex-direction: row;
  min-height: 65vh;
  max-height: 65vh;
  background-color: var(--pdf-bg-primary);
  border-radius: var(--pdf-border-radius);
  position: relative;
  overflow: hidden;
}

/* Panel lateral de miniaturas */
.thumbnail-panel {
  width: 320px;
  min-width: 220px;
  background-color: var(--pdf-bg-primary);
  border-right: 1px solid var(--pdf-border-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0; 
}

.thumbnail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: var(--pdf-bg-white);
  border-bottom: 1px solid var(--pdf-border-color);
  box-shadow: var(--pdf-shadow-light);
}

.thumbnail-title {
  color: var(--pdf-text-primary);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.thumbnail-title::before {
  content: "📄";
  font-size: 16px;
}

.thumbnail-toggle {
  color: #586069;
  font-size: 14px;
  background: none;
  border: none;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.thumbnail-toggle:hover {
  color: #24292e;
  background-color: #f3f4f6;
}

.thumbnail-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 20px 30px 20px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-height: calc(100vh - 150px);
  width: 100%;
  position: relative;
}

/* Scrollbar personalizada para el panel de miniaturas */
.thumbnail-list::-webkit-scrollbar {
  width: 8px;
}

.thumbnail-list::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 4px;
}

.thumbnail-list::-webkit-scrollbar-thumb {
  background: #c1c8cd;
  border-radius: 4px;
  border: 1px solid #f1f3f4;
}

.thumbnail-list::-webkit-scrollbar-thumb:hover {
  background: #a8b2b9;
}

/* Firefox scrollbar */
.thumbnail-list {
  scrollbar-width: thin;
  scrollbar-color: #c1c8cd #f1f3f4;
  background-color: #282828;
}

.thumbnail-item {
  position: relative;
  cursor: pointer;
  overflow: visible;
  border: 2px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: fit-content;
  display: flex;
  flex-direction: column;
  width: fit-content;    
  margin: 0 auto;        
  align-self: center;     
}

.thumbnail-item:hover {
  border-color: #63b3ed;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.thumbnail-item.active {
  border-color: #3182ce;
  box-shadow: 0 0 0 2px #3182ce, 0 4px 8px rgba(49, 130, 206, 0.3);
}

.thumbnail-canvas {
  width: auto;           
  height: auto;
  max-width: 100%;
  max-height: 160px;
  display: block;
  background-color: #282828;
  object-fit: contain;
  margin: 0 auto;
  overflow: hidden;
}

.thumbnail-number {
  position: absolute;
  bottom: -28px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 12px;
  padding: 0;
  font-weight: 400;
  z-index: 20;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}


/* Indicador de scroll */
.scroll-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  margin-top: 8px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #dee2e6;
  color: #6c757d;
  font-size: 11px;
  text-align: center;
}

.scroll-indicator i {
  font-size: 10px;
  opacity: 0.7;
}

.scroll-indicator span {
  line-height: 1.2;
}

/* Contenido principal del PDF */
.pdf-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Contenedor del contenido PDF con miniaturas */
.pdf-content-container {
  flex: 1;
  display: flex;
  overflow: hidden;
  background-color: #282828;
}

.pdf-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to bottom, var(--pdf-bg-white), #fafbfc);
  border-bottom: 1px solid var(--pdf-border-color);
  box-shadow: var(--pdf-shadow-light);
  flex-wrap: wrap;
  gap: 12px;
}

.pdf-toolbar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  width: 100%;
  background: #F0F3F5;
  border-bottom: 1px solid var(--pdf-border-color); 
  box-shadow: var(--pdf-shadow-light); 
  flex-wrap: wrap;
  gap: 12px;
  border-radius: 16px;            
  margin: 2px;                     
}
.toolbar-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-left {
  flex: 1;
  justify-content: flex-start;
}

.toolbar-center {
  flex: 1;
  justify-content: center;
}

.toolbar-right {
  flex: 1;
  justify-content: flex-end;
}

/* Badge de páginas */
.page-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background-color: #f0f3f5;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 500;
  color: #586069;
  min-width: fit-content; 
  white-space: nowrap;
}

.page-badge i {
  color: #e53e3e;
  font-size: 16px;
}

.page-navigation {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap; 
}

.page-label {
  font-size: 12px;
  color: #586069;
}

.page-input {
  width: 40px;
  height: 28px;
  padding: 3px 8px;
  border: 1px solid #d1d5da;
  border-radius: 4px;
  background-color: white;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: #24292e;
  outline: none;
  transition: all 0.2s ease;
}

.page-input:focus {
  border-color: #0366d6;
  box-shadow: 0 0 0 3px rgba(3, 102, 214, 0.1);
}

.page-input:hover:not(:focus) {
  border-color: #959da5;
}

/* Ocultar flechas de input number */
.page-input::-webkit-outer-spin-button,
.page-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.page-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.page-total {
  font-size: 12px;
  color: #586069;
}

/* Controles de zoom */
.zoom-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f6f8fa;
  border-radius: 20px;
  padding: 4px;
}

.zoom-input-container {
  display: flex;
  align-items: center;
  min-width: 60px;
}

.zoom-input {
  width: 50px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: #24292e;
  padding: 5px 8px;
  background-color: white;
  border: 1px solid #d1d5da;
  border-radius: 4px;
  outline: none;
}

.zoom-input:focus {
  border-color: #0366d6;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(3, 102, 214, 0.1);
}

.zoom-percent {
  font-size: 13px;
  font-weight: 600;
  color: #24292e;
  margin-left: 2px;
}

/* Botones de control */
.control-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: #586069;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.control-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
  color: #24292e;
  transform: translateY(-1px);
}

.control-btn:active:not(:disabled) {
  transform: translateY(0);
}

.control-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.control-btn i {
  font-size: 18px;
}

/* Botones de zoom */
.zoom-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  justify-content: center;
}

.zoom-btn:hover:not(:disabled) {
  background-color: #e1e4e8;
  color: #0366d6;
}

/* Botón de acción */
.action-btn:hover:not(:disabled) {
  background-color: #e8f4f8;
  color: #0366d6;
}


/* Texto de botones */
.btn-text {
  font-size: 13px;
}

/* Divisor */
.divider {
  width: 1px;
  height: 24px;
  background-color: #24292e;
  margin: 0 4px;
}

.pdf-content {
  flex: 1;
  overflow: auto;
  display: block;
  min-height: 100%;
  min-width: 0;
  transition: margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 1px solid #525252;
  background-color: #282828;
  box-sizing: border-box;
  text-align: center;
}

.pdf-content-inner {
  display: inline-block;
  padding: 0 20px;
  min-height: 100%;
  box-sizing: border-box;
  vertical-align: top;
  text-align: center; 
}


.pdf-page-container {
  margin-bottom: 5px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
}

.pdf-page-container:last-child {
  margin-bottom: 0;
}

.pdf-content canvas {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  background-color: white;
  display: inline-block; 
  max-width: none;
  max-height: none;
  margin: 10px auto; 
  flex-shrink: 0;
  vertical-align: top;
}

.pdf-loading-inline,
.pdf-error-inline {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #6c757d;
  width: 100%;
  padding: 40px;
}

.pdf-loading-inline p,
.pdf-error-inline p {
  margin: 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 991px) {
  .pdf-toolbar {
    padding: 8px 10px;
    gap: 6px;
  }

  .toolbar-group {
    flex-wrap: nowrap;  
    justify-content: center;
    gap: 4px;
  }
  
  .pdf-toolbar-content {
    padding: 8px 10px;
    gap: 6px;
    border-radius: 12px;
    margin: 1px;
    flex-wrap: nowrap;
    overflow-x: auto;
  }
  
  .pdf-viewer-container {
    min-height: 40vh;
    max-height: 40vh;
  }

  .btn-text {
    display: none;
  }

  .control-btn {
    padding: 6px; 
    font-size: 13px;
  }

  .control-btn i {
    font-size: 14px;
  }

  .action-btn,
  .download-btn,
  .print-btn {
    width: 32px; 
    height: 32px;
    justify-content: center;
  }

  /* inputs más pequeños en móvil */
  .page-input {
    width: 40px;
    height: 24px;
    font-size: 13px;
    padding: 2px 4px;
  }

  .zoom-input-container {
    display: none;
  }

  /* badge más pequeño */
  .page-badge {
    padding: 6px 10px;
    font-size: 13px;
    gap: 6px;
  }

  .divider {
    display: none;
  }
}

/* Media query para móviles muy pequeños */
@media (max-width: 576px) {
  .pdf-toolbar-content {
    padding: 6px 8px;
    gap: 4px;
  }

  .toolbar-group {
    gap: 2px;
  }

  .control-btn {
    padding: 4px;
    font-size: 12px;
  }

  .control-btn i {
    font-size: 12px;
  }

  .action-btn,
  .download-btn,
  .print-btn {
    width: 28px;
    height: 28px;
  }

  .page-input {
    width: 35px;
    height: 22px;
    font-size: 12px;
  }


  .page-badge {
    padding: 4px 8px;
    font-size: 12px;
    gap: 4px;
  }
}

/* Tema claro para el visor PDF para el preview en el diseñador */
.pdf-viewer-light {
  background-color: #ffffff;
  min-height: auto;
  max-height: none;
}

.pdf-viewer-light .pdf-main-content {
  background-color: #ffffff;
}

.pdf-viewer-light .pdf-content-container {
  background-color: #ffffff;
}

.pdf-viewer-light .pdf-content {
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
}

.pdf-viewer-light .pdf-content-inner {
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-width: auto !important;
  min-height: auto !important;
  padding: 0 !important;
}

.pdf-viewer-light .pdf-page-container {
  box-shadow: none;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
}

.pdf-viewer-light canvas {
  display: block;
}
</style>

